// src/components/TaskCompromissos.jsx
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import "./TaskCompromissos.css";
import { gerarIcsCompromisso } from "../utils/gerarIcs";
import EnviarCompromissoEmailModal from "./EnviarCompromissoEmailModal";

const DIAS_SEMANA_CURTO = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
const MESES_CURTO = ["JAN","FEV","MAR","ABR","MAI","JUN","JUL","AGO","SET","OUT","NOV","DEZ"];
const MESES_LONGO = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];
const DIAS_LONGO  = ["Domingo","Segunda-feira","Terça-feira","Quarta-feira","Quinta-feira","Sexta-feira","Sábado"];

const parseDateLocal = (str) => {
  if (!str) return null;
  const [a, m, d] = str.split("-").map(Number);
  return new Date(a, m - 1, d);
};

const formatarHora = (h) => {
  if (!h) return "";
  return h.slice(0, 5); // "HH:MM"
};

const isHoje = (dateStr) => {
  if (!dateStr) return false;
  const hoje = new Date();
  const d    = parseDateLocal(dateStr);
  return d && d.getFullYear() === hoje.getFullYear() &&
    d.getMonth() === hoje.getMonth() && d.getDate() === hoje.getDate();
};

const gerarAbreviacao = (nome) => {
  if (!nome) return "?";
  const p = nome.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : nome.slice(0, 2).toUpperCase();
};


// ── Chip participante ────────────────────────────────────────────
const ChipParticipante = ({ nome, avatarUrl, onRemove }) => (
  <span className="comp-chip">
    {avatarUrl
      ? <img src={avatarUrl} alt={nome} className="comp-chip-img" />
      : <span className="comp-chip-iniciais">{gerarAbreviacao(nome)}</span>
    }
    <span className="comp-chip-nome">{nome}</span>
    {onRemove && <button className="comp-chip-remove" type="button" onClick={onRemove}>×</button>}
  </span>
);

// ── Modal de criar/editar ────────────────────────────────────────
function ModalCompromisso({ notaId, usuarioId, containerId, compromissoEdit, onSave, onClose }) {
  const [titulo,     setTitulo]     = useState(compromissoEdit?.titulo || "");
  const [descricao,  setDescricao]  = useState(compromissoEdit?.descricao || "");
  const [data,       setData]       = useState(compromissoEdit?.data_compromisso || "");
  const [horaInicio, setHoraInicio] = useState(compromissoEdit?.hora_inicio?.slice(0,5) || "");
  const [horaFim,    setHoraFim]    = useState(compromissoEdit?.hora_fim?.slice(0,5) || "");
  const [local,      setLocal]      = useState(compromissoEdit?.local || "");
  const [linkReuniao, setLinkReuniao] = useState(compromissoEdit?.link_reuniao || "");
  const [participantes, setParticipantes] = useState([]);
  const [inputPart,  setInputPart]  = useState("");
  const [sugestoes,  setSugestoes]  = useState([]);
  const [salvando,   setSalvando]   = useState(false);

  useEffect(() => {
    if (!compromissoEdit?.id) return;
    (async () => {
      const { data: parts } = await supabase
        .from("compromisso_participantes")
        .select("id, usuario_id, nome_externo, profiles(nome, avatar_url)")
        .eq("compromisso_id", compromissoEdit.id);
      setParticipantes((parts || []).map(p => ({
        id: p.id,
        usuario_id: p.usuario_id || null,
        nome_externo: p.nome_externo || null,
        nome: p.profiles?.nome || p.nome_externo || "—",
        avatarUrl: p.profiles?.avatar_url || null,
      })));
    })();
  }, [compromissoEdit?.id]);

  const buscarSugestoes = async (termo) => {
    if (!containerId || !termo) { setSugestoes([]); return; }
    const { data: convites } = await supabase
      .from("convites").select("user_id")
      .eq("container_id", containerId).eq("status", "aceito");
    const ids = (convites || []).map(c => c.user_id).filter(Boolean);
    if (!ids.length) { setSugestoes([]); return; }
    const { data: perfis } = await supabase
      .from("profiles").select("id, nome, nickname, avatar_url")
      .in("id", ids)
      .or(`nome.ilike.%${termo}%,nickname.ilike.%${termo}%`)
      .limit(8);
    setSugestoes((perfis || []).filter(p => !participantes.some(x => x.usuario_id === p.id)));
  };

  const handleInput = async (e) => {
    const val = e.target.value;
    setInputPart(val);
    if (val.startsWith("@") && val.length > 1) await buscarSugestoes(val.slice(1));
    else setSugestoes([]);
  };

  const adicionarInterno = (p) => {
    if (participantes.some(x => x.usuario_id === p.id)) return;
    setParticipantes(prev => [...prev, { usuario_id: p.id, nome_externo: null, nome: p.nickname || p.nome || "—", avatarUrl: p.avatar_url || null }]);
    setInputPart(""); setSugestoes([]);
  };

  const adicionarExterno = () => {
    const nome = inputPart.trim();
    if (!nome || nome.startsWith("@")) return;
    if (participantes.some(p => p.nome_externo === nome && !p.usuario_id)) return;
    setParticipantes(prev => [...prev, { usuario_id: null, nome_externo: nome, nome, avatarUrl: null }]);
    setInputPart(""); setSugestoes([]);
  };

  const remover = (idx) => setParticipantes(prev => prev.filter((_, i) => i !== idx));

  const handleSalvar = async () => {
    if (!titulo.trim() || !data) { alert("Título e data são obrigatórios."); return; }
    setSalvando(true);
    try {
      const payload = {
        nota_id: notaId, criado_por: usuarioId,
        titulo: titulo.trim(), descricao: descricao.trim() || null,
        data_compromisso: data,
        hora_inicio: horaInicio || null, hora_fim: horaFim || null,
        local: local.trim() || null,
        link_reuniao: linkReuniao.trim() || null,
        updated_at: new Date().toISOString(),
      };
      let compId;
      if (compromissoEdit?.id) {
        await supabase.from("compromissos").update(payload).eq("id", compromissoEdit.id);
        compId = compromissoEdit.id;
        await supabase.from("compromisso_participantes").delete().eq("compromisso_id", compId);
      } else {
        const { data: novo, error } = await supabase.from("compromissos").insert([payload]).select().single();
        if (error) throw error;
        compId = novo.id;
      }
      if (participantes.length) {
        await supabase.from("compromisso_participantes").insert(
          participantes.map(p => ({ compromisso_id: compId, usuario_id: p.usuario_id || null, nome_externo: p.nome_externo || null }))
        );
      }
      const { data: completo } = await supabase
        .from("compromissos").select("*, profiles:criado_por(nome, nickname)").eq("id", compId).single();
      onSave(completo, participantes, !compromissoEdit?.id);
    } catch (err) {
      console.error(err); alert(`Erro ao salvar: ${err.message}`);
    } finally { setSalvando(false); }
  };

  return (
    <div className="comp-overlay" onClick={onClose}>
      <div className="comp-modal" onClick={e => e.stopPropagation()}>
        <div className="comp-modal-header">
          <span className="material-symbols-outlined" style={{ color: "#7c3aed", fontSize: 20 }}>event</span>
          <h3>{compromissoEdit ? "Editar compromisso" : "Novo compromisso"}</h3>
          <button className="comp-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="comp-modal-body">
          <div className="comp-field">
            <label>Título *</label>
            <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ex: Reunião de alinhamento" autoFocus />
          </div>

          <div className="comp-field">
            <label>Descrição</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descreva o compromisso..." rows={2} />
          </div>

          <div className="comp-field-row">
            <div className="comp-field">
              <label>Data *</label>
              <input type="date" value={data} onChange={e => setData(e.target.value)} />
            </div>
            <div className="comp-field">
              <label>Início</label>
              <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} />
            </div>
            <div className="comp-field">
              <label>Término</label>
              <input type="time" value={horaFim} onChange={e => setHoraFim(e.target.value)} />
            </div>
          </div>

          <div className="comp-field">
            <label>Local</label>
            <input type="text" value={local} onChange={e => setLocal(e.target.value)} placeholder="Sala, Teams, canteiro..." />
          </div>

          <div className="comp-field">
            <label>Link da reunião</label>
            <input 
              type="url" 
              value={linkReuniao} 
              onChange={e => setLinkReuniao(e.target.value)} 
              placeholder="https://meet.google.com/..." 
            />
          </div>

          <div className="comp-field">
            <label>Participantes</label>
            {participantes.length > 0 && (
              <div className="comp-chips-wrap" style={{ marginBottom: 6 }}>
                {participantes.map((p, idx) => (
                  <ChipParticipante key={idx} nome={p.nome} avatarUrl={p.avatarUrl} onRemove={() => remover(idx)} />
                ))}
              </div>
            )}
            <div style={{ position: "relative" }}>
              <input
                type="text" value={inputPart} onChange={handleInput}
                placeholder="@membro ou nome externo + Enter"
                style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 7, padding: "7px 10px", fontSize: 13, fontFamily: "inherit", outline: "none" }}
                onKeyDown={e => {
                  if (e.key === "Enter") { e.preventDefault(); sugestoes.length ? adicionarInterno(sugestoes[0]) : adicionarExterno(); }
                }}
              />
              {sugestoes.length > 0 && (
                <div className="comp-sugestoes">
                  {sugestoes.map(p => (
                    <div key={p.id} className="comp-sugestao-item" onMouseDown={() => adicionarInterno(p)}>
                      {p.avatar_url
                        ? <img src={p.avatar_url} alt={p.nickname || p.nome} className="comp-sugestao-avatar" />
                        : <span className="comp-sugestao-iniciais">{gerarAbreviacao(p.nickname || p.nome)}</span>
                      }
                      <span>{p.nickname || p.nome}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="comp-hint">Digite @nickname para membros, ou nome livre para externos.</p>
          </div>
        </div>

        <div className="comp-modal-footer">
          <button className="comp-btn comp-btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="comp-btn comp-btn--primary" onClick={handleSalvar} disabled={salvando}>
            {salvando ? "Salvando…" : compromissoEdit ? "Salvar alterações" : "Criar compromisso"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────
export default function TaskCompromissos({ notaId, usuarioId, containerId, onNovoComentario }) {
  const [compromissos, setCompromissos] = useState([]);
  const [partsPorComp, setPartsPorComp] = useState({});
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [editando, setEditando]         = useState(null);
  // 3) Estado para controle do modal de envio de convite
  const [enviandoConviteFor, setEnviandoConviteFor] = useState(null);

  // Estado e ref para o menu
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRefs = useRef({});

  // Effect para fechar menu ao clicar fora
  useEffect(() => {
    if (!openMenuId) return;
    const handleClickFora = (e) => {
      const el = menuRefs.current[openMenuId];
      if (el && !el.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleClickFora);
    return () => document.removeEventListener("mousedown", handleClickFora);
  }, [openMenuId]);

  useEffect(() => {
    if (!notaId) return;
    carregar();
  }, [notaId]);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data: comps } = await supabase
        .from("compromissos")
        .select("*, profiles:criado_por(nome, nickname)")
        .eq("nota_id", notaId)
        .order("concluido", { ascending: true })
        .order("data_compromisso", { ascending: true });

      if (!comps?.length) { setCompromissos([]); setPartsPorComp({}); setLoading(false); return; }
      const ids = comps.map(c => c.id);
      const { data: parts } = await supabase
        .from("compromisso_participantes")
        .select("id, compromisso_id, usuario_id, nome_externo, profiles(nome, avatar_url)")
        .in("compromisso_id", ids);

      const pm = (parts || []).reduce((acc, p) => {
        if (!acc[p.compromisso_id]) acc[p.compromisso_id] = [];
        acc[p.compromisso_id].push({
          id: p.id, usuario_id: p.usuario_id, nome_externo: p.nome_externo,
          nome: p.profiles?.nome || p.nome_externo || "—", avatarUrl: p.profiles?.avatar_url || null,
        });
        return acc;
      }, {});

      setCompromissos(comps); setPartsPorComp(pm);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async (comp, parts, isNovo) => {
    setCompromissos(prev =>
      isNovo
        ? [...prev, comp].sort((a, b) => a.data_compromisso.localeCompare(b.data_compromisso))
        : prev.map(c => c.id === comp.id ? comp : c)
    );
    setPartsPorComp(prev => ({ ...prev, [comp.id]: parts }));
    setShowModal(false); setEditando(null);

    if (onNovoComentario) {
      const nomeCriador = comp.profiles?.nickname || comp.profiles?.nome || "Alguém";
      const d = parseDateLocal(comp.data_compromisso);
      const dataFmt = d
        ? `${DIAS_LONGO[d.getDay()]}, ${d.getDate()} de ${MESES_LONGO[d.getMonth()]} de ${d.getFullYear()}`
        : comp.data_compromisso;
      const horaFmt  = comp.hora_inicio ? ` às ${formatarHora(comp.hora_inicio)}` : "";
      const partStr  = parts.length ? ` com ${parts.map(p => p.nome).join(", ")}` : "";
      const acao     = isNovo ? "agendou" : "atualizou";
      await onNovoComentario(`📅 ${nomeCriador} ${acao} o compromisso "${comp.titulo}" para ${dataFmt}${horaFmt}${partStr}.`);
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Excluir este compromisso?")) return;
    await supabase.from("compromissos").delete().eq("id", id);
    setCompromissos(prev => prev.filter(c => c.id !== id));
    setPartsPorComp(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const toggleConcluido = async (compId, concluidoAtual) => {
    const novoConcluido = !concluidoAtual;
    const agora = new Date().toISOString();
    // Otimista: atualiza localmente imediatamente
    setCompromissos(prev => prev.map(c =>
      c.id === compId
        ? { ...c, concluido: novoConcluido, concluido_em: novoConcluido ? agora : null }
        : c
    ));
    try {
      const { error } = await supabase
        .from("compromissos")
        .update({ concluido: novoConcluido, concluido_em: novoConcluido ? agora : null })
        .eq("id", compId);
      if (error) throw error;
    } catch (err) {
      console.error("Erro ao salvar conclusão:", err);
      // Reverte em caso de erro
      setCompromissos(prev => prev.map(c =>
        c.id === compId
          ? { ...c, concluido: concluidoAtual, concluido_em: c.concluido_em }
          : c
      ));
    }
  };

  // Agrupa por data
  const grupos = compromissos.reduce((acc, c) => {
    const key = c.data_compromisso;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  return (
    <div className="comp-root">
      {/* Header */}
      <div className="comp-section-header">
        <div className="comp-section-header-left">
          <span className="comp-section-title">Compromissos</span>
          {compromissos.length > 0 && <span className="comp-counter">{compromissos.length}</span>}
        </div>
        <button className="comp-btn-add" onClick={() => { setEditando(null); setShowModal(true); }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
          Novo
        </button>
      </div>

      {loading ? (
        <div className="comp-loading">
          <span className="material-symbols-outlined comp-spin">progress_activity</span>
        </div>
      ) : compromissos.length === 0 ? (
        <p className="comp-empty">Nenhum compromisso agendado.</p>
      ) : (
        <div className="comp-agenda">
          {Object.entries(grupos).map(([dateStr, items]) => {
            const d      = parseDateLocal(dateStr);
            const dia    = d ? d.getDate() : "—";
            const mes    = d ? MESES_CURTO[d.getMonth()] : "";
            const semana = d ? DIAS_SEMANA_CURTO[d.getDay()] : "";
            const hoje   = isHoje(dateStr);

            return (
              <div key={dateStr} className="comp-grupo">
                {/* Coluna data */}
                <div className="comp-data-col">
                  <span className="comp-data-dia">{dia} {mes}</span>
                  <span className="comp-data-semana">{semana}</span>
                  {hoje && <span className="comp-badge-hoje">Hoje</span>}
                </div>

                {/* Linha vertical + cards */}
                <div className="comp-linha-wrap">
                  <div className="comp-linha-vertical" />
                  <div className="comp-cards-col">
                    {items.map(c => {
                      const parts     = partsPorComp[c.id] || [];
                      const concluido = c.concluido || false;
                      const hora      = c.hora_inicio ? formatarHora(c.hora_inicio) : "";
                      const hasLink   = c.link_reuniao?.trim();

                      return (
                        <div key={c.id} className={`comp-agenda-card${concluido ? " comp-agenda-card--concluido" : ""}`}>
                          {/* 2) Header substituído com botão de enviar convite */}
                          <div className="comp-agenda-card-header">
                            <span className="material-symbols-outlined comp-agenda-icon">event_note</span>
                            <span className="comp-agenda-titulo">{c.titulo}</span>
                            {c.local && (
                              <span className="comp-agenda-local">
                                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>location_on</span>
                                {c.local}
                              </span>
                            )}
                            {hasLink && (
                              <a 
                                href={c.link_reuniao} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="comp-link-reuniao"
                                title="Abrir link da reunião"
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>videocam</span>
                              </a>
                            )}
                            {/* 2) Botão de enviar convite por e-mail */}
                            <button
                              className="comp-agenda-enviar-btn"
                              title="Enviar convite por e-mail"
                              onClick={e => { 
                                e.stopPropagation(); 
                                setEnviandoConviteFor(c); 
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>mail</span>
                            </button>
                          </div>

                          {/* Linha dos itens */}
                          <div className="comp-agenda-item-row">
                            <input
                              type="checkbox"
                              className="comp-agenda-check"
                              checked={concluido}
                              onChange={() => toggleConcluido(c.id, c.concluido)}
                            />
                            <span className={`comp-agenda-item-texto${concluido ? " comp-agenda-item-texto--riscado" : ""}`}>
                              {c.descricao || c.titulo}
                            </span>

                            <div className="comp-agenda-meta">
                              {hora && (
                                <span className="comp-agenda-meta-item">
                                  <span className="material-symbols-outlined" style={{ fontSize: 13 }}>schedule</span>
                                  {hora}
                                </span>
                              )}
                              {parts.length > 0 && (
                                <span className="comp-agenda-meta-item">
                                  <span className="material-symbols-outlined" style={{ fontSize: 13 }}>person</span>
                                  {parts.map(p => p.nome.split(" ")[0]).join(", ")}
                                </span>
                              )}
                              {/* Botão de menu */}
                              <div
                                className="comp-agenda-menu-wrap"
                                ref={el => { menuRefs.current[c.id] = el; }}
                              >
                                <button
                                  className="comp-agenda-menu-btn"
                                  title="Opções"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setOpenMenuId(prev => prev === c.id ? null : c.id);
                                  }}
                                >
                                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>more_vert</span>
                                </button>

                                {openMenuId === c.id && (
                                  <div className="comp-inline-menu">
                                    <button onClick={() => { setEditando(c); setShowModal(true); setOpenMenuId(null); }}>
                                      Editar
                                    </button>
                                    <button
                                      style={{ color: "#dc2626" }}
                                      onClick={() => { handleExcluir(c.id); setOpenMenuId(null); }}
                                    >
                                      Excluir
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <ModalCompromisso
          notaId={notaId} usuarioId={usuarioId} containerId={containerId}
          compromissoEdit={editando} onSave={handleSave}
          onClose={() => { setShowModal(false); setEditando(null); }}
        />
      )}

      {/* 3) Modal de envio de convite por e-mail */}
      {enviandoConviteFor && (
        <EnviarCompromissoEmailModal
          compromisso={enviandoConviteFor}
          participantes={partsPorComp[enviandoConviteFor.id] || []}
          usuarioId={usuarioId}  // ← ADICIONE ESTA LINHA
          containerId={containerId}
          onClose={() => setEnviandoConviteFor(null)}
        />
      )}
    </div>
  );
}